import React from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';

import { SubscriptionStatus } from '$/GraphQL/schema/types';
import { useThemeContext } from '$/components/ui/ThemeContext';

import { logger } from '$/utils/logger';

import { PriceBar } from './Components';
import { usePlanPageContext } from '../PlanPage';
import { UpdateAddOnType, useAddOnsContext } from './AddOnsContext';

import type { AddOn } from './Info/AddOn';
import Form from '$/components/ui/Form';
import Text from '$/components/ui/Text';
import Button from '$/components/ui/Button';

export const getCardComponent = (addOn: AddOn): React.FC => {
  const CardComponent: React.FC = () => {
    const { openModal } = useAddOnsContext();
    const { team, billingCycle } = usePlanPageContext();
    const etheme = useThemeContext();
    const isSuspended = [SubscriptionStatus.Incomplete, SubscriptionStatus.Suspended].includes(
      team.subscriptionStatus
    );

    const onClick = React.useCallback(async () => {
      if (isSuspended) return;
      if (!addOn.priceTier) {
        logger.error('Missing priceTier in add-on ', addOn.name);
        return;
      }
      openModal({
        addOn,
        tierId: addOn.priceTier.tierId,
        type: UpdateAddOnType.Subscribe,
      });
    }, [openModal, isSuspended]);

    const Icon = addOn.Icon;
    const formattedPrice = addOn.getFormattedPrice(billingCycle);
    return (
      <Form
        css={css`
          width: 100%;
          @media only screen and (min-width: 992px) {
            width: 350px;
            margin: 0.5rem 0.5rem 1rem;
          }
        `}
        onSubmit={onClick}
      >
        <div
          className='flex flex-col justify-between bg-white relative opacity-100 text-gray-0 cursor-pointer'
          css={css`
            ${tw`
            transition-all duration-200
            border border-gray-300 hover:shadow-lg rounded-lg
          `}

            height: 350px;
            margin: 0.5rem 0 1rem;
            width: 100%;

            overflow: hidden;
            min-height: 20rem;

            padding: 0;
          `}
          onClick={onClick}
        >
          <div
            className='flex w-full items-center justify-center border-b border-gray-300'
            css={css`
              min-height: 150px;
              background-size: cover;
              background-position: top center;
              background-image: url(${addOn.image()});
              position: relative;
            `}
          >
            {!addOn.image() && <Icon />}
            <PriceBar price={formattedPrice} billingCycle={billingCycle} />
          </div>
          <div className='flex flex-col justify-between py-1/2e px-1e h-full'>
            <div className='flex flex-col'>
              <Text.headline className='flex justify-between text-xl font-medium mt-1/4e mb-3/4e w-full'>
                <div>{addOn.name}</div>
              </Text.headline>
              <div className='text-md mt-0 leading-relaxed'>{addOn.description}</div>
            </div>
            <div className='mt-1e mb-1'>
              <Button.rounded
                theme={etheme.controls.button.action}
                className='w-full'
                onClick={onClick}
                disabled={isSuspended}
              >
                <span>{addOn.getCtaButtonText(UpdateAddOnType.Subscribe)}</span>
              </Button.rounded>
            </div>
          </div>
        </div>
        {addOn.trialEnd && (
          <div className='mt-1e text-center text-xs text-gray-600'>
            Free trial ended {addOn.trialEnd.format('P')}
          </div>
        )}
      </Form>
    );
  };
  return CardComponent;
};
